<template>
  <div class="role-details-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <base-nav
        :title="roleFromStore?.name || 'Роль'"
        :show-back="true"
        :heading-level="3"
        back-fallback="PermissionRoles"
        :badge="
          roleFromStore?.system
            ? {
                text: 'Системная роль',
                color: '#1d4ed8',
                bg: '#dbeafe',
              }
            : null
        "
      >
        <base-primary-button
          v-if="roleFromStore?.system"
          class="nav-btn"
          :disabled="isLoading"
          @click="confirmReset"
        >
          <span v-if="isLoading">Загрузка...</span>
          <span v-else>Сбросить по умолчанию</span>
        </base-primary-button>
      </base-nav>

      <div v-if="groupedGrid.length === 0" class="no-data">
        Нет данных о правах
      </div>

      <div v-else class="permissions-layout">
        <BaseTabs
          v-model="activeGroup"
          :tabs="groupTabs"
          :show-default-context-item="hideGroupTabContextMenu"
          @tab-click="handleGroupTabClick"
        />

        <!-- Поиск по всем разделам -->
        <div class="search-wrap">
          <svg
            class="search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>

          <input
            v-model="resourceSearch"
            class="search-input"
            type="text"
            placeholder="Поиск по всем разделам..."
          />

          <button
            v-if="resourceSearch"
            class="search-clear"
            type="button"
            aria-label="Очистить поиск"
            @click="clearResourceSearch"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Предупреждение -->
        <div v-if="hasAnyIndexDisabled" class="global-warning">
          <div class="global-warning-icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"
                fill="currentColor"
              />
              <rect
                x="11"
                y="7"
                width="2"
                height="6"
                rx="1"
                fill="#fff"
              />
              <rect
                x="11"
                y="15"
                width="2"
                height="2"
                rx="1"
                fill="#fff"
              />
            </svg>
          </div>

          <div class="global-warning-text">
            <strong>Внимание:</strong>
            отключение прав «Список» и «Просмотр» для некоторых
            разделов может привести к тому, что на некоторых страницах
            не будет отображаться необходимая информация. Будьте особо
            осторожны при настройке этого права.
          </div>
        </div>

        <div class="group-content">
          <!-- Режим поиска: совпадения по всем разделам -->
          <template v-if="resourceSearch.trim()">
            <div
              v-if="globalSearchResults.length === 0"
              class="no-data"
            >
              Ничего не найдено
            </div>

            <div v-else class="resource-list">
              <PermissionRoleResourceCard
                v-for="resource in globalSearchResults"
                :key="resource.target_resource"
                :resource="resource"
                :disabled="isLoading"
                @toggle-permission="handleToggle"
                @change-data-scope="handleDataScopeChange"
              />
            </div>
          </template>

          <!-- Обычный режим: список прав активной группы -->
          <template v-else>
            <template v-for="group in groupedGrid" :key="group.group">
              <div v-if="activeGroup === group.group">
                <div class="resource-list">
                  <PermissionRoleResourceCard
                    v-for="resource in group.resources"
                    :key="resource.target_resource"
                    :resource="resource"
                    :disabled="isLoading"
                    @toggle-permission="handleToggle"
                    @change-data-scope="handleDataScopeChange"
                  />
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
    </template>

    <Teleport to="body">
      <base-dialog
        :visible="showResetDialog"
        title="Сбросить права по умолчанию?"
        :message="`Все права роли «${roleFromStore?.name}» будут сброшены до системных значений по умолчанию. Это действие нельзя отменить.`"
        :actions="resetDialogActions"
        :close-on-overlay="true"
        @action="handleResetDialogAction"
      />
    </Teleport>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";

import { getCompanyId, getUserData } from "@/utils/userUtils";

import { useNotificationStore } from "@/stores/notificationStore";
import { usePermissionRolesStore } from "@/stores/permissionRolesStore";
import { usePermissionsStore } from "@/stores/permissionsStore";

import PermissionRoleResourceCard from "@/components/PermissionRole/ResourceCard.vue";

export default {
  name: "PermissionRoleDetailsPage",

  components: { PermissionRoleResourceCard },

  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      dataIsLoading: true,
      activeGroup: null,
      resourceSearch: "",
      showResetDialog: false,
      resetDialogActions: [
        {
          key: "confirm",
          label: "Сбросить",
          type: "danger",
        },
        {
          key: "close",
          label: "Отмена",
          type: "secondary",
        },
      ],
    };
  },

  computed: {
    ...mapState(usePermissionRolesStore, ["grid", "isLoading"]),

    notificationStore() {
      return useNotificationStore();
    },

    roleFromStore() {
      return usePermissionRolesStore().getRoleById(this.id);
    },

    groupedGrid() {
      return this.grid.groups || [];
    },

    groupTabs() {
      return this.groupedGrid.map((group) => ({
        key: group.group,
        label: group.group_label,
        icon: group.group_emoji || "📁",
      }));
    },

    allResources() {
      return this.grid.resources || [];
    },

    globalSearchResults() {
      const query = this.resourceSearch.trim().toLowerCase();

      if (!query) return [];

      return this.allResources.filter((resource) =>
        resource.label.toLowerCase().includes(query)
      );
    },

    hasAnyIndexDisabled() {
      return this.allResources.some((resource) =>
        this.isIndexDisabled(resource)
      );
    },
  },

  watch: {
    groupedGrid(groups) {
      const activeGroupExists = groups.some(
        (group) => group.group === this.activeGroup
      );

      if (groups.length > 0 && !activeGroupExists) {
        this.activeGroup = groups[0].group;
      }
    },
  },

  methods: {
    ...mapActions(usePermissionRolesStore, [
      "fetchGrid",
      "fetchRoles",
      "togglePermission",
      "updateResourceScope",
      "resetToDefault",
    ]),

    hideGroupTabContextMenu() {
      return false;
    },

    isIndexDisabled(resource) {
      const indexPermission = resource.permissions?.find(
        (permission) => permission.action === "index"
      );

      return indexPermission ? !indexPermission.value : false;
    },

    handleGroupTabClick() {
      this.resourceSearch = "";
    },

    clearResourceSearch() {
      this.resourceSearch = "";
      this.activeGroup = this.groupedGrid[0]?.group || null;
    },

    confirmReset() {
      this.showResetDialog = true;
    },

    handleResetDialogAction(actionKey) {
      if (actionKey === "confirm") {
        this.handleResetToDefault();
      }

      this.showResetDialog = false;
    },

    async handleToggle(targetResource, permissionAction) {
      try {
        await this.togglePermission(
          this.id,
          targetResource,
          permissionAction
        );

        const userData = getUserData();
        const companyId = getCompanyId();

        const myRoleId = userData?.company_users?.find(
          (companyUser) => companyUser.company_id === companyId
        )?.permission_role_id;

        if (myRoleId && String(this.id) === String(myRoleId)) {
          await usePermissionsStore().loadPermissions();
        }

        this.notificationStore.showSuccess("Права обновлены");
      } catch {
        // Ошибки обрабатываются в store.
      }
    },

    async handleDataScopeChange(targetResource, dataScope) {
      await this.updateResourceScope(this.id, targetResource, dataScope);
      this.notificationStore.showSuccess("Видимость данных обновлена");
    },

    async handleResetToDefault() {
      try {
        await this.resetToDefault(this.id);

        this.notificationStore.showSuccess(
          "Права сброшены по умолчанию"
        );
      } catch {
        // Ошибки обрабатываются в store.
      }
    },
  },

  async created() {
    try {
      this.dataIsLoading = true;

      const store = usePermissionRolesStore();

      if (store.roles.length === 0) {
        await this.fetchRoles();
      }

      await this.fetchGrid(this.id);

      if (this.groupedGrid.length > 0) {
        this.activeGroup = this.groupedGrid[0].group;
      }
    } finally {
      this.dataIsLoading = false;
    }
  },
};
</script>

<style scoped>
.role-details-container {
  flex-grow: 1;
}

.no-data {
  padding: 20px 0;
  color: var(--color-dark);
  opacity: 0.45;
}

.permissions-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.global-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid var(--color-yellow);
  border-radius: 10px;
  background: #fefce8;
}

.global-warning-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--color-yellow);
}

.global-warning-text {
  color: var(--color-dark);
  font-size: 14px;
  line-height: 1.55;
  opacity: 0.85;
}

.global-warning-text strong {
  color: var(--color-yellow);
  font-weight: 600;
  opacity: 1;
}

.search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 320px;
  margin: var(--base-in-indent) 0;
}

.search-icon {
  position: absolute;
  left: 10px;
  flex-shrink: 0;
  color: #9ca3af;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 8px 32px 8px 34px;
  border: 1px solid var(--color-grey);
  border-radius: 8px;
  outline: none;
  background: var(--color-light);
  color: var(--color-dark);
  font-size: 14px;
  transition: border-color 0.15s;
}

.search-input:focus {
  border-color: var(--color-blue);
}

.search-clear {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.15s;
}

.search-clear:hover {
  color: var(--color-dark);
}

.group-content {
  flex: 1;
  min-width: 0;
}

.resource-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@media (max-width: 768px) {
  .search-wrap {
    width: 100%;
    max-width: 100%;
  }
}
</style>
